import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import BugsnagPerformance from "@bugsnag/browser-performance";

const apiKey = process.env.NEXT_PUBLIC_BUGSNAG_API_KEY;

// Allow overriding, but default to detecting localhost so local dev can be separated even
// when NEXT_PUBLIC_ENV is set to "development" for API compatibility.
const resolveReleaseStage = () => {
    const override = process.env.NEXT_PUBLIC_BUGSNAG_RELEASE_STAGE;
    if (override) return override;

    const env = process.env.NEXT_PUBLIC_ENV || process.env.NODE_ENV;
    const hostname =
        typeof window !== "undefined" ? window.location.hostname : undefined;
    const isLocalHost =
        hostname?.startsWith("localhost") || hostname?.startsWith("127.");

    if (isLocalHost) return "local";
    if (env) return env;
    return "development";
};

const releaseStage = resolveReleaseStage();

// Simpler
// const releaseStage =
//     process.env.NEXT_PUBLIC_ENV || process.env.NODE_ENV || "development";

const enabledReleaseStages = [
    "local",
    "dev",
    "qa",
    "uat",
    "prod",
    "production",
    "staging",
    "development",
];

let started = false;
let perfStarted = false;

const ensureStarted = () => {
    if (started || !apiKey) {
        return;
    }

    Bugsnag.start({
        apiKey,
        releaseStage,
        enabledReleaseStages,
        appVersion: process.env.NEXT_PUBLIC_APP_VERSION,
        autoTrackSessions: true,
        appType: "browser",
        plugins: [new BugsnagPluginReact()],
        onError: (event) => {
            const href =
                typeof window !== "undefined"
                    ? window.location.href
                    : undefined;

            event.addMetadata("environment", {
                releaseStage,
                env: process.env.NEXT_PUBLIC_ENV,
                hostname:
                    typeof window !== "undefined"
                        ? window.location.hostname
                        : undefined,
            });

            if (href) {
                event.addMetadata("page", {
                    href,
                });
            }
        },
    });

    started = true;

    if (!perfStarted) {
        // Start performance monitoring with same API key; keep it non-blocking.
        BugsnagPerformance.start({
            apiKey,
            releaseStage,
            enabledReleaseStages,
        });
        perfStarted = true;
    }
};

export const ensureBugsnagStarted = () => {
    ensureStarted();
    return started;
};

export const getBugsnagErrorBoundary = (react: typeof import("react")) => {
    const isStarted = ensureBugsnagStarted();
    if (!isStarted) return undefined;

    const plugin = Bugsnag.getPlugin("react");
    return plugin?.createErrorBoundary(react);
};

export const notifyBugsnag = (
    error: unknown,
    metadata?: Record<string, unknown>
) => {
    ensureStarted();
    if (!started) return;

    const err = error instanceof Error ? error : new Error(String(error));
    Bugsnag.notify(err, (event) => {
        if (metadata) {
            event.addMetadata("metadata", metadata);
        }
    });
};

export const isBugsnagEnabled = () => Boolean(apiKey);
